import React, { Suspense, useEffect, useState } from 'react'
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { authService } from './services/auth'
import { queryClient } from './services/queryClient'
import Layout from './components/Layout'
import { logConfig } from './config'
import { initializeGA, trackLogin } from './utils/analytics'
import { register as registerSW } from './utils/serviceWorker'
import AnalyticsTracker from './components/AnalyticsTracker'
import DemoBanner from './components/DemoBanner'
import { useTokenRefresh } from './hooks/useTokenRefresh'
import './App.css'
import './i18n'
import i18n from './i18n'

// Code splitting with React.lazy - load components only when needed
const Dashboard = React.lazy(() => import('./pages/Dashboard'))
const DailyLoadDetail = React.lazy(() => import('./pages/charts/DailyLoadDetail'))
const TariffPeriodDetail = React.lazy(() => import('./pages/charts/TariffPeriodDetail'))
const MonthlyConsumptionDetail = React.lazy(() => import('./pages/charts/MonthlyConsumptionDetail'))
const MaxPowerDetail = React.lazy(() => import('./pages/charts/MaxPowerDetail'))
const Acessibilidade = React.lazy(() => import('./pages/Acessibilidade'))

// Loading component for suspense
const LoadingSpinner: React.FC = () => {
  const { t } = useTranslation()
  return (
    <div className="loading-container">
      <div className="loading">{t('loading')}</div>
    </div>
  )
}

const App: React.FC = () => {
  const { t } = useTranslation()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)

  // Set up proactive token refresh once authenticated
  useTokenRefresh()

  useEffect(() => {
    initializeAuth()
  }, [])

  const initializeAuth = async () => {
    try {
      // Log configuration for debugging
      logConfig()

      // Initialize Google Analytics
      await initializeGA()

      // Register service worker for better performance
      registerSW({
        onSuccess: () => {
          console.log('Service Worker registered successfully')
        },
        onUpdate: () => {
          console.log('Service Worker updated')
        }
      })

      const authenticated = await authService.init()
      setIsAuthenticated(authenticated)

      // Set language based on user's preference after authentication
      if (authenticated) {
        const userLanguage = authService.getUserLanguage()
        await i18n.changeLanguage(userLanguage)

        // Track successful login
        trackLogin()
      }
    } catch (error) {
      console.error('Authentication initialization failed:', error)
      setIsAuthenticated(false)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">{t('loading')}</div>
      </div>
    )
  }

  // Render public and authenticated routes
  return (
    <QueryClientProvider client={queryClient}>
      <DemoBanner />
      <Router>
        <AnalyticsTracker />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public route - accessible without authentication */}
            <Route path="/acessibilidade" element={<Acessibilidade />} />

            {/* Authenticated routes */}
            {!isAuthenticated ? (
              <Route path="*" element={
                <div className="login-container">
                  <div className="login-box">
                    <h1>{t('appName')}</h1>
                    <p>{t('accessDenied')}</p>
                    <button onClick={() => authService.login()}>{t('login')}</button>
                  </div>
                </div>
              } />
            ) : (
              <Route path="*" element={
                <Layout>
                  <Routes>
                    <Route path="/dashboard/:contractId?" element={<Dashboard />} />
                    <Route path="/dashboard/:contractId/daily-load/:date?" element={<DailyLoadDetail />} />
                    <Route path="/dashboard/:contractId/tariff-period/:date?" element={<TariffPeriodDetail />} />
                    <Route path="/dashboard/:contractId/monthly-consumption" element={<MonthlyConsumptionDetail />} />
                    <Route path="/dashboard/:contractId/max-power" element={<MaxPowerDetail />} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Layout>
              } />
            )}
          </Routes>
        </Suspense>
      </Router>
      {/* React Query Devtools - only in development */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
